import MatchedMap from 'utilities/src/matchedMap';

import type { IIconsProps, SizeType } from '../../../../../Icon';

import { InputSizeEnum } from '../../../utils/size';
import { InputStateEnum } from '../../../utils/state';
import { InputVariantsEnum } from '../../../utils/variants';
import { LabelPositionEnum, InputTypeEnum, type TInputType } from '../../../utils/types';
import { InputThemeEnum, type InputThemeProps } from '../../../utils/themes';

/**
 * Common Classes
 */

const fieldClasses = 'gap-3 items-center bg-white px-4 py-[14px] paragraph-s text-neutrals-800';

const defaultFieldClassesMap = new MatchedMap([
  [LabelPositionEnum.Top, ''],
  [LabelPositionEnum.In, '[&_input]:h-0'],
]);

/**
 * Icon Classes
 */

const fieldIconClasses = 'fill-neutrals-800';

/**
 * Focus Classes
 */

const focusFieldInputClassesMap = new MatchedMap([
  [InputTypeEnum.Input, 'focus-within:py-1 [&:focus-within_input]:h-auto'],
  [InputTypeEnum.Textarea, ''],
]);

const focusFieldClassesMap = (inputType: TInputType) =>
  new MatchedMap([
    [LabelPositionEnum.Top, ''],
    [LabelPositionEnum.In, focusFieldInputClassesMap.get(InputTypeEnum[inputType])],
  ]);

/**
 * Filled Classes
 */

const filledFieldInputClassesMap = new MatchedMap([
  [InputTypeEnum.Input, '!py-1 [&_input]:h-auto'],
  [InputTypeEnum.Textarea, ''],
]);

const filledLabelClassesMap = new MatchedMap([
  [InputTypeEnum.Input, '[&_.label]:font-medium [&_.label]:paragraph-xs [&_.label]:leading-5'],
  [InputTypeEnum.Textarea, '[&_.label]:font-medium [&_.label]:paragraph-xs [&_.label]:pb-[2px]'],
]);

const filledFieldClassesMap = (inputType: TInputType) =>
  new MatchedMap([
    [LabelPositionEnum.Top, ''],
    [
      LabelPositionEnum.In,
      filledFieldInputClassesMap
        .get(InputTypeEnum[inputType])
        .concat(' ', filledLabelClassesMap.get(InputTypeEnum[inputType])),
    ],
  ]);

/**
 * Theme State Classes
 */

const defaultStateClassesMap = new MatchedMap([
  [InputStateEnum.Default, 'outline-offset-[-1px] focus-within:outline-2'],
  [InputStateEnum.Disabled, 'outline-offset-[-1px] focus-within:outline-2 '],
  [InputStateEnum.Error, '!outline-2 outline-offset-[-2px]'],
  [InputStateEnum.Success, '!outline-2 outline-offset-[-2px]'],
]);

const darkStateClassesMap = new MatchedMap([
  [InputStateEnum.Default, 'outline-neutrals-300 hover:outline-neutrals-500'],
  [InputStateEnum.Disabled, 'outline-neutrals-300 hover:outline-neutrals-500 '],
  [
    InputStateEnum.Error,
    'outline-danger-500 hover:outline-danger-500 focus-within:outline-danger-500 focus-visible:outline-danger-500',
  ],
  [
    InputStateEnum.Success,
    'outline-success-600 hover:outline-success-600 focus-within:outline-success-600 focus-visible:outline-success-600',
  ],
]);

const lightStateClassesMap = new MatchedMap([
  [InputStateEnum.Default, 'outline-neutrals-300 hover:outline-neutrals-500'],
  [InputStateEnum.Disabled, 'outline-neutrals-300 hover:outline-neutrals-500'],
  [
    InputStateEnum.Error,
    'outline-danger-700 hover:outline-danger-700 focus-within:outline-danger-700 focus-visible:outline-danger-700',
  ],
  [
    InputStateEnum.Success,
    'outline-success-600 hover:outline-success-600 focus-within:outline-success-600 focus-visible:outline-success-600',
  ],
]);

const themeStateClassesMap = new MatchedMap([
  [InputThemeEnum.Dark, darkStateClassesMap],
  [InputThemeEnum.Light, lightStateClassesMap],
]);

/**
 * Theme Variant Classes
 */

const darkVariantClassesMap = new MatchedMap([
  [InputVariantsEnum.Primary, 'outline-offset-[-2px] focus-within:!outline-primary-600'],
  [InputVariantsEnum.Secondary, 'outline-offset-[-2px] focus-within:!outline-secondaryPurple-600'],
]);

const lightVariantClassesMap = new MatchedMap([
  [InputVariantsEnum.Primary, 'outline-offset-[-2px] focus-within:!outline-primary-600'],
  [InputVariantsEnum.Secondary, 'outline-offset-[-2px] focus-within:!outline-secondaryPurple-600'],
]);

const themeVariantClassesMap = new MatchedMap([
  [InputThemeEnum.Dark, darkVariantClassesMap],
  [InputThemeEnum.Light, lightVariantClassesMap],
]);

/**
 * Theme Field Icons
 */

const fieldStateIconMap = new MatchedMap<InputStateEnum, IIconsProps['iconName']>([
  [InputStateEnum.Default, undefined],
  [InputStateEnum.Disabled, undefined],
  [InputStateEnum.Error, 'Warning'],
  [InputStateEnum.Success, 'Check'],
]);

const fieldIconSize = new MatchedMap<InputSizeEnum, SizeType>([
  [InputSizeEnum.Small, 'IconS'],
  [InputSizeEnum.Medium, 'IconS'],
  [InputSizeEnum.Large, 'IconM'],
]);

/**
 * Blog Theme
 */

const inputFieldBlogTheme = ({
  inputType,
  inputState,
  inputVariant,
  inputTheme,
  inputSize,
  labelPosition,
}: InputThemeProps) => ({
  fieldThemeClasses: fieldClasses,

  fieldBehaviorDefaultThemeClasses: defaultFieldClassesMap.get(LabelPositionEnum[labelPosition]),
  fieldBehaviorFocusThemeClasses: focusFieldClassesMap(inputType).get(LabelPositionEnum[labelPosition]),
  fieldBehaviorFilledThemeClasses: filledFieldClassesMap(inputType).get(LabelPositionEnum[labelPosition]),

  fieldStateThemeClasses: defaultStateClassesMap
    .get(InputStateEnum[inputState])
    .concat(' ', themeStateClassesMap.get(InputThemeEnum[inputTheme]).get(InputStateEnum[inputState])),

  fieldSizeThemeClasses: '',
  fieldBackgroundThemeClasses: '',

  fieldVariantThemeClasses: themeVariantClassesMap
    .get(InputThemeEnum[inputTheme])
    .get(InputVariantsEnum[inputVariant]),

  fieldIconThemeClasses: fieldIconClasses,
  fieldIconStateThemeClasses: '',

  fieldThemeStateIcon: fieldStateIconMap.get(InputStateEnum[inputState]),
  fieldIconThemeSize: fieldIconSize.get(InputSizeEnum[inputSize]),
});

export default inputFieldBlogTheme;
